import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, Banknote, FileText, ChevronRight } from "lucide-react";

const STEPS = [
  { key: "identity", stage: "onboarding", title: "Identité de l'entreprise", desc: "Nom légal, SIREN, adresse, représentant légal.", icon: ShieldCheck },
  { key: "documents", stage: "documents_required", title: "Justificatifs", desc: "Pièce d'identité du représentant + justificatif d'adresse.", icon: FileText },
  { key: "bank", stage: "charges_enabled", title: "Compte bancaire", desc: "IBAN pour recevoir vos virements Stripe.", icon: Banknote },
];

export default function StripeOnboarding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const acct = params.get("acct");

  useEffect(() => {
    api.post("/stripe/connect/simulate", { stage: "onboarding" }).catch(() => {});
  }, []);

  const advance = async () => {
    setBusy(true);
    try {
      const current = STEPS[step];
      await api.post("/stripe/connect/simulate", { stage: current.stage });
      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        await api.post("/stripe/connect/complete", {});
        setDone(true);
        toast.success("Compte Stripe activé : paiements et virements opérationnels");
        setTimeout(() => navigate("/vendor/parametrages?section=payments"), 1400);
      }
    } catch {
      toast.error("Erreur pendant l'onboarding");
    }
    setBusy(false);
  };

  const quitLater = async () => {
    await api.post("/stripe/connect/simulate", { stage: STEPS[Math.max(0, step - 0)].stage }).catch(() => {});
    navigate("/vendor/parametrages?section=payments");
  };

  return (
    <VendorLayout>
      <div className="max-w-2xl mx-auto p-6 sm:p-10">
        <Breadcrumb
          items={[
            { label: "Pilotage", href: "/vendor/pilotage" },
            { label: "Paramétrages", href: "/vendor/parametrages?section=payments" },
            { label: "Onboarding Stripe" },
          ]}
          className="mb-5"
        />
        <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10" data-testid="stripe-onboarding-wizard">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#635BFF] text-white flex items-center justify-center font-bold text-lg">S</div>
            <div>
              <div className="overline">Stripe Connect Express</div>
              <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900">Configuration de votre compte</h1>
            </div>
          </div>

          <div className="mt-5 rounded-2xl bg-violet-50 border border-violet-100 p-4 text-xs text-slate-700 leading-relaxed">
            <span className="font-semibold text-violet-800 uppercase tracking-wider text-[10px] block mb-1">Mode test simulé</span>
            Ce parcours reproduit l'onboarding hébergé Stripe. En Sandbox/Live, chaque étape sera le formulaire officiel Stripe — l'expérience Junago restera identique.
          </div>

          {/* Étapes */}
          <div className="mt-7 space-y-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const state = i < step ? "done" : i === step ? "current" : "todo";
              return (
                <div key={s.key}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${state === "current" ? "border-violet-300 bg-violet-50/50" : state === "done" ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 opacity-60"}`}
                  data-testid={`onboarding-step-${s.key}`}>
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${state === "done" ? "bg-emerald-100 text-emerald-600" : "bg-violet-100 text-violet-700"}`}>
                    {state === "done" ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{s.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                  </div>
                  {state === "current" && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold shrink-0">En cours</span>}
                </div>
              );
            })}
          </div>

          {acct && (
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-[11px] text-slate-600 font-mono break-all">
              Stripe Account ID : {acct}
            </div>
          )}

          <button
            onClick={advance} disabled={busy || done}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-semibold text-sm bg-[#635BFF] hover:bg-[#544ae6] transition-colors disabled:opacity-50"
            data-testid="onboarding-next-btn">
            {done ? (<><CheckCircle2 className="h-4 w-4" /> Compte activé !</>)
              : busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Validation…</>)
              : step < STEPS.length - 1 ? (<>Valider « {STEPS[step].title} » <ChevronRight className="h-4 w-4" /></>)
              : (<>Terminer l'onboarding Stripe</>)}
          </button>

          <button onClick={quitLater} disabled={busy}
            className="mt-3 w-full px-6 py-3 rounded-xl border border-slate-200 text-sm font-medium hover:border-slate-900"
            data-testid="onboarding-quit-btn">
            Terminer plus tard
          </button>
        </div>
      </div>
    </VendorLayout>
  );
}
